import React from "react";

import { Line, Text, Circle } from "react-konva";
import { useTheme } from "@mui/material";

import { RULER_HEIGHT_IN_PX } from "../../constants";
import { formatMillisecondsToHHMMSSForRuler } from "../../../../../utils/formatMillisecondsToHHMMSSForRuler";

interface RulerProps {
  totalMilliseconds: number;
  pixelsPerMillisecond: number;
}

export default function Ruler({
  totalMilliseconds,
  pixelsPerMillisecond,
}: RulerProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const textColor = isDark ? "#aaa" : "#222";
  const dotColor = isDark ? "#555" : "#bbb";
  const lineColor = isDark ? "#444" : "#000";

  const majorStepIntervals = [
    1000, 5000, 15000, 30000, 60000, 300000, 600000, 900000, 1800000, 3600000,
  ];

  // Find an appropriate major step size based on the zoom level (pixelsPerMillisecond)
  const majorStepSize =
    majorStepIntervals.find(
      (interval) => interval * pixelsPerMillisecond >= 50, // Ensure at least 50 pixels between major steps
    ) || majorStepIntervals[majorStepIntervals.length - 1];

  const majorSteps = [];

  // Always show 0s mark first
  const zeroText = formatMillisecondsToHHMMSSForRuler(0);
  const zeroTextWidth = zeroText.length * 7;
  majorSteps.push(
    <Text
      key={0}
      x={Math.max(5, 0 - zeroTextWidth / 2)} // Ensure it's not cut off on the left
      y={25}
      text={zeroText}
      fontSize={14}
      fontStyle="bold"
      fill={textColor}
    />,
  );

  // Add dots between 0s and first major step
  Array.from({ length: 5 }).forEach((_, dotIndex) => {
    const dotX = (dotIndex + 1) * (majorStepSize / 6) * pixelsPerMillisecond;
    if (dotX <= totalMilliseconds * pixelsPerMillisecond) {
      majorSteps.push(
        <Circle
          key={`dot-${dotX}`}
          x={dotX}
          y={RULER_HEIGHT_IN_PX - 5}
          radius={2}
          fill={dotColor}
        />,
      );
    }
  });

  // Add major steps starting from majorStepSize
  for (
    let i = majorStepSize;
    i <= totalMilliseconds + majorStepSize;
    i += majorStepSize
  ) {
    const x = i * pixelsPerMillisecond;
    const text = formatMillisecondsToHHMMSSForRuler(i);
    const textWidth = text.length * 7; // Slightly larger font

    // Only add if there's enough space and it's within bounds
    if (x - textWidth / 2 >= 0 && i <= totalMilliseconds) {
      majorSteps.push(
        <React.Fragment key={i}>
          <Text
            x={x - textWidth / 2}
            y={25}
            text={text}
            fontSize={14}
            fontStyle="bold"
            fill={textColor}
          />

          {/* Adding 5 evenly spaced dots between major steps */}
          {Array.from({ length: 5 }).map((_, dotIndex) => {
            const dotX =
              x + (dotIndex + 1) * (majorStepSize / 6) * pixelsPerMillisecond;
            return (
              dotX <= totalMilliseconds * pixelsPerMillisecond && (
                <Circle
                  key={dotX}
                  x={dotX}
                  y={RULER_HEIGHT_IN_PX - 5}
                  radius={2}
                  fill={dotColor}
                />
              )
            );
          })}
        </React.Fragment>,
      );
    }
  }

  return (
    <>
      <Line
        points={[
          0,
          RULER_HEIGHT_IN_PX,
          totalMilliseconds * pixelsPerMillisecond,
          RULER_HEIGHT_IN_PX,
        ]}
        stroke={lineColor}
      />
      {majorSteps}
    </>
  );
}
